import React, { useState, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import {
  LayoutGrid,
  Lightbulb,
  BookOpen,
  ChevronLeft,
  Home,
  Settings,
  Menu,
  X,
  MessageSquare,
  Shield,
  Network,
  Flame
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { employeeNamesService } from '../../services/employeeNames';
import { UserInfoSection } from '../layout/UserInfoSection';
import { FeedbackModal } from '../shared/FeedbackModal';
import '../../styles/dialog-select.css';

const IdeaMachineLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  
  // State Management
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Idea Machine';
    
    if (path === '/idea-machine' || path === '/idea-machine/dashboard') {
      title = 'Idea Machine';
    } else if (path.startsWith('/idea-machine/launchpad')) {
      title = 'Idea Launchpad | Idea Machine';
    } else if (path.startsWith('/idea-machine/ideas')) {
      title = 'Your Ideas | Idea Machine';
    } else if (path.startsWith('/idea-machine/feed')) {
      title = 'Idea Feed | Idea Machine';
    } else if (path.startsWith('/idea-machine/settings')) {
      title = 'Settings | Idea Machine';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch display name
  useEffect(() => {
    const getDisplayName = async () => {
      if (userEmail) {
        try {
          const name = await employeeNamesService.getNameFromEmail(userEmail);
          setDisplayName(name || userEmail.split('@')[0]);
        } catch (error) {
          console.error('Error fetching display name:', error);
          setDisplayName(userEmail.split('@')[0]);
        }
      }
    };
    getDisplayName();
  }, [userEmail]);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const { isAdmin } = useIsAdmin();

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-900 border-b dark:border-gray-800 z-40 flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <span className="font-semibold text-gray-900 dark:text-white">Idea Machine</span>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:relative z-50 h-full bg-white dark:bg-gray-900 border-r dark:border-gray-800 shadow-sm flex flex-col transition-all duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0 w-64' : 'translate-x-[-100%] md:translate-x-0'}
          ${!isMobileMenuOpen && isCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Mobile Close Button */}
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="absolute top-4 right-4 p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md md:hidden"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Desktop Collapse Toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden md:flex absolute -right-3 top-8 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-full p-1 shadow-md hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 z-50 transition-colors"
        >
          <ChevronLeft className={`h-4 w-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="border-b dark:border-gray-800 flex justify-center items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200 py-6"
          onClick={() => handleNavigate('/idea-machine/dashboard')}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <div className="flex flex-col items-center">
              <Lightbulb className="h-8 w-8 text-yellow-500 transition-all duration-300" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-4 text-center">
              <div className="bg-yellow-100 dark:bg-yellow-900/20 p-3 rounded-full mb-3">
                <Lightbulb className="h-8 w-8 text-yellow-600 dark:text-yellow-500" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Idea Machine</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Track & Invent</p>
            </div>
          )}
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 mt-6 overflow-y-auto flex flex-col gap-1 px-3">
          <div
            onClick={() => handleNavigate('/idea-machine/dashboard')}
            className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
              isActive('/idea-machine/dashboard')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={isCollapsed ? "Dashboard" : ""}
          >
            <LayoutGrid className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/dashboard') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Dashboard</span>}
          </div>

          <div
            onClick={() => handleNavigate('/idea-machine/launchpad')}
            className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
              isActive('/idea-machine/launchpad')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={isCollapsed ? "Idea Launchpad" : ""}
          >
            <Lightbulb className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/launchpad') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Idea Launchpad</span>}
          </div>

          <div
            onClick={() => handleNavigate('/idea-machine/galaxy')}
            className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
              isActive('/idea-machine/galaxy')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={isCollapsed ? "Idea Galaxy" : ""}
          >
            <Network className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/galaxy') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Idea Galaxy</span>}
          </div>

          <div
            onClick={() => handleNavigate('/idea-machine/feed')}
            className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
              isActive('/idea-machine/feed')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={isCollapsed ? "Idea Feed" : ""}
          >
            <Flame className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/feed') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Idea Feed</span>}
          </div>

          <div
            onClick={() => handleNavigate('/idea-machine/ideas')}
            className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
              isActive('/idea-machine/ideas')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={isCollapsed ? "Your Ideas" : ""}
          >
            <BookOpen className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/ideas') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Your Ideas</span>}
          </div>

          <div
            onClick={() => {
              setIsFeedbackModalOpen(true);
              setIsMobileMenuOpen(false);
            }}
            className="flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all duration-200 group"
            title={isCollapsed ? "Give Feedback" : ""}
          >
            <MessageSquare className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Give Feedback</span>}
          </div>

          {/* Divider */}
          <div className="mx-2 my-2 border-t border-gray-100 dark:border-gray-800" />

          {/* Settings */}
          <div
            onClick={() => handleNavigate('/idea-machine/settings')}
            className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
              isActive('/idea-machine/settings')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
            }`}
            title={isCollapsed ? "Settings" : ""}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/settings') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Settings</span>}
          </div>

          {/* Admin Link */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/idea-machine/admin')}
              className={`flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg transition-all duration-200 group ${
                isActive('/idea-machine/admin')
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
              }`}
              title={isCollapsed ? "Admin" : ""}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 transition-colors ${isActive('/idea-machine/admin') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'}`} />
              {(!isCollapsed || isMobileMenuOpen) && <span>Admin</span>}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-3 py-2.5 text-sm cursor-pointer rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all duration-200 group"
            title={isCollapsed ? "Apps Hub" : ""}
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5 text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300`} />
            {(!isCollapsed || isMobileMenuOpen) && <span>Apps Hub</span>}
          </div>
        </nav>

        {/* User Info Section */}
        <UserInfoSection 
          displayName={displayName} 
          isCollapsed={isCollapsed && !isMobileMenuOpen} 
        />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto bg-gray-50 dark:bg-black pt-16 md:pt-0">
        <Outlet />
      </main>

      {/* Feedback Modal */}
      <FeedbackModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        appName="Idea Machine"
      />
    </div>
  );
};

export default IdeaMachineLayout;
